import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';

import { useTileForm } from './useTileForm';

import {
  DirectionalButtonLeft,
  DirectionalButtonRight,
  ArrowWrapper,
} from '../FullSizeTile/components/DirectionalButtons';
import HeadlineInput from './components/HeadlineInput';
import Attachments from '../Attachments/Attachments';
import DisclaimerText from './components/DisclaimerText';
import TileSettings from './components/TileSettings';
import RadioButtonForm from './components/RadioButtonForm';
import Description from './components/Description';
import MediaUploader from './components/MediaUploader';
import Interaction from './components/Interaction';
import { PointsSlider } from './components/Slider';
import AnonymousBannerComponent from '../FullSizeTile/components/AnonymousBannerComponent';
import Hr from '../Hr';

import { InteractionContext } from './contexts';
import { useGetAudienceAndRibbonTag } from './data';

import { TileActionMenu } from '../TileActionMenu/TileActionMenu';

const TileForm = ({
  tile,
  onSave,
  onClose,
  campaignId,
  postId,
  goToPreview,
  nextTile,
  prevTile,
  isFirstTile,
  isLastTile,
  arrowRightRef,
  arrowLeftRef,
  position,
}) => {
  const {
    values,
    handleChange,
    inputError,
    handleBlur,
    addAttachment,
    removeAttachment,
    handleShowDisclaimer,
    handleQuestionSubTypeOnChange,
    handleQuestionOnChange,
    handleAddAnswer,
    handleRemoveAnswer,
    handleMediaChange,
    handlePoints,
    removeMedia,
    admins,
    errors,
    setInitialValues,
    saveAndNavigate,
    autoSaved,
  } = useTileForm({ tile, onSave, postId, position });

  const tileAudienceAndRibbon = useGetAudienceAndRibbonTag();
  const { audiences } = tileAudienceAndRibbon;
  const ribbonTags = tileAudienceAndRibbon.ribbon_tags;
  const hasRibbonTags = ribbonTags && ribbonTags.length > 0;
  const hasAudiences = audiences && audiences.length > 0;

  const { t } = useTranslation('tileForm');
  const { isAnonymous } = values;

  return (
    <>
      <TileActionMenu
        currentPage="edit"
        onClose={() => saveAndNavigate(onClose)}
        uuid={values.id}
        campaignId={campaignId}
        goToPreview={() => saveAndNavigate(goToPreview)}
        campaignContainer={values.campaignContainer}
        publishDate={values.publishDate}
        tileStatus={values.status}
        postId={values.postId}
        autoSaved={autoSaved}
      />
      <FormWrapper>
        <MediaUploader
          values={values}
          handleChange={handleMediaChange}
          removeMedia={removeMedia}
        />
        <FormSection>
          <HeadlineInput
            name="headline"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.headline}
            error={inputError('headline')}
          />
          <Hr />
          <Description
            name="supportingContent"
            onChange={handleChange}
            onInitialValue={normalizedContent => {
              setInitialValues(prevState => {
                return { ...prevState, supportingContent: normalizedContent };
              });
            }}
            value={values.supportingContent}
            error={errors.supportingContent}
          />
          <Hr />
          <DisclaimerText
            toggleName="showDisclaimerText"
            textName="disclaimerText"
            handleChange={handleChange}
            handleShowDisclaimer={handleShowDisclaimer}
            showDisclaimerText={
              values.showDisclaimerText || !!values.disclaimerText
            }
            disclaimerText={values.disclaimerText}
          />
          <Attachments
            attachments={values.attachments}
            removeAttachment={removeAttachment}
            addAttachment={addAttachment}
          />
          {isAnonymous && <AnonymousBannerComponent margin="24px 0" />}
          <InteractionContext.Provider
            value={{
              values,
              handleChange,
              handleAddAnswer,
              handleRemoveAnswer,
              handleQuestionSubTypeOnChange,
              handleQuestionOnChange,
              admins,
              errors,
            }}
          >
            <Interaction />
          </InteractionContext.Provider>
          <Hr />
          <PointsSlider points={values.points} handleChange={handlePoints} />
          <Hr />
          <TileSettings
            isSharable={values.isSharable}
            isPublic={values.isPublic}
            handleChange={handleChange}
          />
          {hasAudiences && (
            <React.Fragment>
              <Hr />
              <RadioButtonForm
                title={t('audience.title')}
                name={t('audience.name')}
                handleChange={handleChange}
                currentValue={values.audienceId}
                radioOptions={audiences}
              />
            </React.Fragment>
          )}
          {hasRibbonTags && (
            <React.Fragment>
              <Hr />
              <RadioButtonForm
                title={t('topic.title')}
                name={t('topic.name')}
                handleChange={handleChange}
                currentValue={values.ribbonTagId}
                radioOptions={ribbonTags}
              />
            </React.Fragment>
          )}

          <ArrowWrapper $hasSidePadding={false}>
            {prevTile && (
              <DirectionalButtonLeft
                prevTile={e => {
                  e.preventDefault();
                  saveAndNavigate(prevTile);
                }}
                ref={arrowLeftRef}
                hidden={isFirstTile}
              />
            )}
            {nextTile && (
              <DirectionalButtonRight
                nextTile={e => {
                  e.preventDefault();
                  saveAndNavigate(nextTile);
                }}
                ref={arrowRightRef}
                hidden={isLastTile}
              />
            )}
          </ArrowWrapper>
        </FormSection>
      </FormWrapper>
    </>
  );
};

export default TileForm;

TileForm.propTypes = {
  arrowLeftRef: PropTypes.object,
  arrowRightRef: PropTypes.object,
  campaignId: PropTypes.string,
  currentPosition: PropTypes.number,
  currentPositionInPost: PropTypes.number,
  goToPreview: PropTypes.func,
  isFirstTile: PropTypes.bool,
  isLastTile: PropTypes.bool,
  nextTile: PropTypes.func,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  postTilesCount: PropTypes.number,
  prevTile: PropTypes.func,
  setImageTop: PropTypes.func,
  tile: PropTypes.object,
  tileCount: PropTypes.number,
  postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 600px;
  border-radius: 8px;
  position: relative;
  margin: auto;
  margin-top: 20px;
  background: ${themeGet('colors.white')};
`;

const FormSection = styled.section`
  padding: 0 24px;
`;
